// useFieldIds — small hook that returns a getter for stable, unique-per-form
// field ids.  Solves the app-wide WCAG gap where <label> is a sibling of
// <input> without htmlFor/id association.  Retrofit pattern:
//
//   const fid = useFieldIds()
//   <label htmlFor={fid('email')}>Email</label>
//   <input id={fid('email')} ... />
//
// The returned ids are stable across renders for the same field name +
// scoped to this component instance via React's useId().  Two forms on
// the same page can safely use `email` as a field name — they'll get
// different ids because their useId() prefixes differ.
//
// Preserves existing form structure — doesn't require refactoring to the
// <Field> wrapper.  Use <Field> for NEW forms; use this hook to retrofit
// existing ones.

import { useId, useCallback } from 'react'

export default function useFieldIds () {
  const prefix = useId()
  // Sanitize prefix (useId returns like `:r0:`, colons are valid HTML5 but
  // trip some CSS selectors + downstream libs).
  const safePrefix = prefix.replace(/:/g, '')
  return useCallback(
    (fieldName) => `${safePrefix}-${String(fieldName).replace(/[^a-zA-Z0-9_-]/g, '-')}`,
    [safePrefix]
  )
}
